class RepeaterRecommendations extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
  }

  connectedCallback() {
    this.initializeRecommendations();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initializeRecommendations() {
    // Using the logic used in the native Shopify product-recommendations component
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  removeLeftoverPlaceholders(grid) {
    if (!grid) return;
    grid.querySelectorAll('.repeater-recommendation-placeholder').forEach((p) => {
      const li = p.closest('li');
      if (li) li.remove();
    });
  }

  /**
   * Strips card elements based on hide flags before DOM insertion.
   */
  applyHideFlags(li, hideFlags) {
    if (hideFlags.collectionLabel === 'true') {
      li.querySelectorAll('[data-card-collection-label]').forEach((el) => el.remove());
    }
    if (hideFlags.coffeeAttributes === 'true') {
      li.querySelectorAll('[data-card-coffee-attributes]').forEach((el) => el.remove());
    }
    if (hideFlags.flavorDescription === 'true') {
      li.querySelectorAll('[data-card-flavor-description]').forEach((el) => el.remove());
    }
    if (hideFlags.flavorType === 'true') {
      li.querySelectorAll('[data-card-flavor-type]').forEach((el) => el.remove());
    }
    if (hideFlags.price === 'true') {
      li.querySelectorAll('[data-card-price]').forEach((el) => el.remove());
    }
  }

  loadRecommendations() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const remainingSlots = parseInt(this.dataset.remainingSlots, 10);
    const swipeOnMobile = this.dataset.swipeOnMobile === 'true';

    // Get exclude handles to check them for deduplication later
    const excludeHandles = (this.dataset.excludeHandles || '')
      .split(',')
      .map((h) =>
        h
          .trim()
          .toLowerCase()
          .replace(/<\/?sup>/gi, '')
          .replace(/[\u00AE\u2122]/g, '')
          .replace(/-{2,}/g, '-')
          .replace(/^-|-$/g, '')
      )
      .filter((h) => h.length > 0);

    // Find the parent grid (ul element)
    const grid = this.closest('ul.repeater-related-items-grid');

    if (!grid || !productId || !sectionId) {
      this.remove();
      return;
    }

    const hideFlags = {
      collectionLabel: this.dataset.hideCollectionLabel,
      coffeeAttributes: this.dataset.hideCoffeeAttributes,
      flavorDescription: this.dataset.hideFlavorDescription,
      flavorType: this.dataset.hideFlavorType,
      price: this.dataset.hidePrice,
    };

    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}`;

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;

        // Find all <li> elements in the response
        const recommendedItems = html.querySelectorAll('.repeater-recommendations-content > li');
        const placeholders = grid.querySelectorAll('.repeater-recommendation-placeholder');

        let count = 0;
        recommendedItems.forEach((item) => {
          if (count >= remainingSlots) return;

          // Check for duplicates: find product handle in the recommended item
          // The card-product snippet includes a link with the product URL
          const productLink = item.querySelector('a[href*="/products/"]');
          if (productLink && excludeHandles.length > 0) {
            const href = productLink.getAttribute('href') || '';
            const handleMatch = href.match(/\/products\/([^/?#]+)/);
            if (handleMatch) {
              // Normalize: URL-decode, strip ® ™, collapse double-hyphens
              const productHandle = decodeURIComponent(handleMatch[1])
                .toLowerCase()
                .replace(/[\u00AE\u2122]/g, '')
                .replace(/-{2,}/g, '-')
                .replace(/^-|-$/g, '');
              // Skip this product - it's a duplicate of a manual override
              if (excludeHandles.includes(productHandle)) {
                return;
              }
            }
          }

          const li = item.cloneNode(true);

          this.applyHideFlags(li, hideFlags);

          if (swipeOnMobile && !li.classList.contains('slider__slide')) {
            li.classList.add('slider__slide');
          }

          // If there is a placeholder, replace it. Otherwise append.
          if (placeholders[count]) {
            const placeholderLi = placeholders[count].closest('li');
            if (placeholderLi) {
              placeholderLi.replaceWith(li);
            } else {
              grid.insertBefore(li, this);
            }
          } else {
            grid.insertBefore(li, this);
          }
          count++;
        });

        // Remove any leftover empty placeholders that didn't get a product
        this.removeLeftoverPlaceholders(grid);

        this.remove();
      })
      .catch((e) => {
        this.removeLeftoverPlaceholders(grid);
        this.remove();
      });
  }
}

if (!customElements.get('repeater-recommendations')) {
  customElements.define('repeater-recommendations', RepeaterRecommendations);
}
